import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query } from "lit/decorators.js";
import { pdIcons } from "@progressive-development/pd-icon";
import "@progressive-development/pd-icon/pd-icon";
import "@progressive-development/pd-forms/pd-button";
//#region src/pd-popup-dialog/PdPopupDialog.ts
/**
* @license
* Copyright (c) 2021 PD Progressive Development UG. All rights reserved.
*/
/**
* Modal dialog with header, content, and configurable footer buttons.
*
* Rendert intern ein natives `<dialog>` und öffnet via `showModal()` im
* Top-Layer des Browsers (immun gegen `transform`/`filter`/`contain`-
* Vorfahren und `overflow`-Clipping; Fokus-Trap und `inert`-Hintergrund
* kommen vom Browser).
*
* Usage contract: instantiation == open — der Dialog öffnet sich beim
* Mounten automatisch (Konsumenten wie pd-confirm-dialog rendern ihn
* konditional). `showPopup()`/`hidePopup()` steuern ihn programmatisch.
*
* Anzeige-Variante: Ohne `buttons` entfällt die Fußzeile komplett —
* mit `withCloseIcon` schließt der Dialog über das X im Kopf (plus
* ESC/Backdrop, wenn erlaubt). Jeder Schließ-Weg feuert `popup-close`,
* damit konditional rendernde Konsumenten ihren Zustand nachziehen.
*
* @tagname pd-popup-dialog
* @summary Modal dialog with header, content slot, and footer buttons.
*
* @event submit-button-clicked - Fired when a footer button is clicked. Emits `{button}`.
* @event popup-close - Fired on every close path (X, ESC, backdrop, hidePopup).
*
* @slot content - Main dialog content.
*
* @cssprop --pd-popup-modal-bg-rgba - Backdrop background (`::backdrop`).
* @cssprop --pd-popup-modal-padding - Modal padding. Default: `--pd-spacing-md`.
* @cssprop --pd-popup-max-width - Maximum dialog width. Default: `700px`.
* @cssprop --pd-popup-header-col - Header background. Default: `--pd-default-col`.
* @cssprop --pd-popup-header-height - Header min-height. Default: `45px`.
* @cssprop --pd-popup-header-gap - Header gap between icon and text. Default: `--pd-spacing-sm`.
* @cssprop --pd-popup-header-font-size - Header font size. Default: `1.3em`.
* @cssprop --pd-popup-header-font-col - Header text color. Default: `--pd-default-bg-col`.
* @cssprop --pd-popup-content-bg - Content area background. Default: `--pd-default-bg-col`.
* @cssprop --pd-popup-content-padding - Content area padding. Default: `--pd-spacing-md`.
* @cssprop --pd-popup-footer-col - Footer background. Default: `--pd-default-light-col`.
* @cssprop --pd-popup-footer-padding - Footer padding. Default: `--pd-spacing-md`.
* @cssprop --pd-popup-footer-button-width - Footer button width. Default: `38%`.
* @cssprop --pd-popup-border-col - Border color of the dialog surface. Default: `--pd-default-light-col`.
* @cssprop --pd-popup-border-radius - Dialog border radius. Default: `var(--pd-radius-lg)`.
*/
var PdPopupDialog = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.type = "info";
		this.title = "Popup Title";
		this.buttons = [];
		this.closeByEscape = false;
		this.closeByBackdrop = false;
		this.blockScroll = false;
		this.withCloseIcon = false;
		this._scrollBlocked = false;
	}
	static {
		this.styles = [css`
      :host {
        display: block;
        --_dialog-radius: var(--pd-popup-border-radius, var(--pd-radius-lg));
      }

      /* Der Dialog selbst ist ein transparenter Vollbild-Container (früher
       * .modal): Klicks neben .modal-content treffen so das Dialog-Element
       * → Backdrop-Close. UA-Defaults max-width/max-height aufheben. Das
       * display nur im offenen Zustand setzen, sonst überschreibt es das
       * display:none des UA-Stylesheets für geschlossene Dialoge. */
      dialog {
        position: fixed;
        inset: 0;
        box-sizing: border-box;
        width: 100%;
        height: 100%;
        max-width: none;
        max-height: none;
        border: none;
        margin: 0;
        padding: var(--pd-popup-modal-padding, var(--pd-spacing-md));
        background: transparent;
        overflow: auto;
        /* Das UA-Stylesheet malt ein dialog-Element in CanvasText und
           unterbricht damit die Vererbung: Eine App, die ihre Textfarbe
           einmal am Host setzt, bekommt im Dialog trotzdem Schwarz — und
           kann es von außen nicht korrigieren, weil die Regel in diesem
           Shadow Root steht. */
        color: inherit;

        overflow-x: hidden;
      }

      dialog[open] {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      /* Custom Properties erben seit Baseline 2024 in ::backdrop. */
      dialog::backdrop {
        background-color: var(
          --pd-popup-modal-bg-rgba,
          var(--pd-modal-overlay-col)
        );
      }

      .modal-content {
        margin: auto;
        padding: 0;
        width: 100%;
        max-width: var(--pd-popup-max-width, 700px);
        border-radius: var(--_dialog-radius);
        border: var(--pd-border-width) solid
          var(--pd-popup-border-col, var(--pd-default-light-col));
        box-shadow: var(--pd-shadow-xl);
        background: var(--pd-default-bg-col);
      }

      /* Mobile: align to top for full scrollability */
      @media (max-width: 767px) {
        dialog[open] {
          align-items: flex-start;
        }
      }

      .header {
        display: flex;
        gap: var(--pd-popup-header-gap, var(--pd-spacing-sm));
        align-items: center;
        background-color: var(--pd-popup-header-col, var(--pd-default-col));
        min-height: var(--pd-popup-header-height, 45px);
        padding: var(--pd-spacing-sm);

        border-top-left-radius: var(--_dialog-radius);
        border-top-right-radius: var(--_dialog-radius);
      }

      .header-txt {
        flex: 1;
        min-width: 0;
        font-family: var(--pd-default-font-title-family);
        font-size: var(--pd-popup-header-font-size, 1.3em);
        color: var(--pd-popup-header-font-col, var(--pd-default-bg-col));
        overflow-wrap: break-word;
        word-break: break-word;
      }

      .close-icon {
        flex: none;
        cursor: pointer;
        --pd-icon-size: 1.4rem;
        --pd-icon-col: var(
          --pd-popup-header-font-col,
          var(--pd-default-bg-col)
        );
        --pd-icon-col-hover: var(
          --pd-popup-header-font-col,
          var(--pd-default-bg-col)
        );
      }

      .close-icon:focus-visible {
        outline: 2px solid
          var(--pd-popup-header-font-col, var(--pd-default-bg-col));
        outline-offset: 2px;
        border-radius: var(--pd-radius-sm, 2px);
      }

      .info-logo {
        width: 2em;
      }

      .error {
        --pd-icon-col: var(--pd-default-error-col);
      }

      .info,
      .help {
        --pd-icon-col: var(--pd-default-info-col);
      }

      .warn {
        --pd-icon-col: var(--pd-default-warning-col);
      }

      .content {
        padding: var(--pd-popup-content-padding, var(--pd-spacing-md));
        background-color: var(--pd-popup-content-bg, var(--pd-default-bg-col));
        height: 100%;
      }

      /* Anzeige-Variante ohne Fußzeile: der Inhalt trägt die unteren Ecken. */
      .content--footless {
        border-bottom-left-radius: var(--_dialog-radius);
        border-bottom-right-radius: var(--_dialog-radius);
      }

      .footer {
        display: flex;
        background: linear-gradient(
          to right,
          var(--pd-popup-footer-col, var(--pd-default-light-col)) 0%,
          var(--pd-default-disabled-light-col) 100%
        );
        align-items: center;
        justify-content: space-around;

        padding: var(--pd-popup-footer-padding, var(--pd-spacing-md));

        border-bottom-left-radius: var(--_dialog-radius);
        border-bottom-right-radius: var(--_dialog-radius);

        --pd-button-width: 100%;
      }

      .footer pd-button {
        width: var(--pd-popup-footer-button-width, 38%);
      }

      /* Mobile: stack buttons vertically */
      @media (max-width: 767px) {
        .footer {
          flex-direction: column;
          gap: var(--pd-spacing-sm);
        }

        .footer pd-button {
          width: 90%;
        }
      }
    `];
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this._unblockScroll();
	}
	firstUpdated() {
		this.showPopup();
	}
	render() {
		const titleId = `${this.id || "popup"}-title`;
		return html`
      <dialog
        id="modalId"
        aria-labelledby="${titleId}"
        @cancel=${this._onCancel}
        @close=${this._onDialogClose}
        @click=${this._onBackdropClick}
      >
        <div class="modal-content">
          <div class="header">
            ${this._renderIcon()}
            <span id="${titleId}" class="header-txt">${this.title}</span>
            ${this.withCloseIcon ? html`
                    <pd-icon
                      icon=${pdIcons.ICON_CANCEL}
                      class="close-icon"
                      tabindex="0"
                      role="button"
                      aria-label="Close dialog"
                      @click=${this.hidePopup}
                      @keydown=${this._onCloseIconKeyDown}
                    ></pd-icon>
                  ` : nothing}
          </div>
          <div
            class="content ${this.buttons.length ? "" : "content--footless"}"
          >
            <slot name="content"></slot>
          </div>
          ${this.buttons.length ? html`
                  <div class="footer">
                    ${this.buttons.map((bt) => html`
                        <pd-button
                          data-key=${bt.key}
                          text=${bt.name}
                          ?disabled=${bt.disabled}
                          ?primary=${bt.primary}
                          @button-clicked=${this._onButtonClick}
                        ></pd-button>
                      `)}
                  </div>
                ` : nothing}
        </div>
      </dialog>
    `;
	}
	_onCloseIconKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this.hidePopup();
		}
	}
	_onButtonClick(e) {
		const key = e.target.dataset.key;
		this.dispatchEvent(new CustomEvent("submit-button-clicked", {
			detail: { button: key },
			bubbles: true,
			composed: true
		}));
	}
	_renderIcon() {
		switch (this.type) {
			case "none": return nothing;
			case "warn": return html`<pd-icon
          icon="warningIconFld"
          class="info-logo warn"
        ></pd-icon>`;
			case "error": return html`<pd-icon
          icon="errorIconFld"
          class="info-logo error"
        ></pd-icon>`;
			case "help": return html`<pd-icon
          icon="helpIconFld"
          class="info-logo help"
        ></pd-icon>`;
			default: return html`<pd-icon
          icon="infoIconFld"
          class="info-logo info"
        ></pd-icon>`;
		}
	}
	/**
	* Öffnet das Popup-Dialog manuell
	*/
	showPopup() {
		if (!this._dialog || this._dialog.open) return;
		this._dialog.showModal();
		if (this.blockScroll && !this._scrollBlocked) {
			document.body.style.overflow = "hidden";
			this._scrollBlocked = true;
		}
	}
	/**
	* Schließt das Popup-Dialog manuell
	*/
	hidePopup() {
		this._dialog?.close();
	}
	/** ESC löst auf dem Dialog ein cancel aus — nur zulassen, wenn erlaubt. */
	_onCancel(e) {
		if (!this.closeByEscape) e.preventDefault();
	}
	_onBackdropClick(e) {
		if (this.closeByBackdrop && e.target === this._dialog) this._dialog.close();
	}
	/** Zentraler Schließ-Pfad: feuert bei ESC, Backdrop, X und close(). */
	_onDialogClose() {
		this._unblockScroll();
		this.dispatchEvent(new CustomEvent("popup-close", {
			bubbles: true,
			composed: true
		}));
	}
	_unblockScroll() {
		if (this._scrollBlocked) {
			document.body.style.overflow = "";
			this._scrollBlocked = false;
		}
	}
};
__decorate([property({ type: String })], PdPopupDialog.prototype, "type", void 0);
__decorate([property({ type: String })], PdPopupDialog.prototype, "title", void 0);
__decorate([property({ type: Array })], PdPopupDialog.prototype, "buttons", void 0);
__decorate([property({ type: Boolean })], PdPopupDialog.prototype, "closeByEscape", void 0);
__decorate([property({ type: Boolean })], PdPopupDialog.prototype, "closeByBackdrop", void 0);
__decorate([property({ type: Boolean })], PdPopupDialog.prototype, "blockScroll", void 0);
__decorate([property({ type: Boolean })], PdPopupDialog.prototype, "withCloseIcon", void 0);
__decorate([query("#modalId")], PdPopupDialog.prototype, "_dialog", void 0);
//#endregion
export { PdPopupDialog };
